import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import {
  hideMany,
  setShowHidden as setShowHiddenAction,
  toggleAllImageAssets,
  toggleHide
} from '../../slice/ImageAssetSlice';
import { canUseEditSuite } from '../../assetFeatureFlags';
import {
  bulkSelection,
  deletableSelection,
  displayedPhotos,
  hasCheckedAssets,
  isInPool,
  stageableSelection
} from '../../model/assetViewScope';
import { persistAssetPatch } from '../../edit/assetEditSeams';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';

// The Photos tab's selection/bulk-action redux logic, shared by
// PhotosLeadingActions (the always-visible select-all/count/download/upload
// cluster), PhotosMenuItems (the kebab: Show Hidden, Show Image Details, and
// the sub-lg Download/Upload mirrors) and AssetsSelectionBar (the floating bar
// the selection-scoped actions moved to in August: Download, Services, Stage
// with AI, Hide, Support, Trash). All three need the same selection math, so it
// lives here rather than being derived three times.
//
// Every pool below comes from model/assetViewScope — the same module the grids,
// the select-all reducers and the page's action handlers read. Counts here are
// that module's lists' `.length`, so a count and the action it labels cannot
// disagree (the shape of all three bugs the PR #1154 review found).
//
// The pool is the tab's (DV-3735): the Floor Plan tab runs the same controls
// over its floor plans only.
export function usePhotosActions() {
  const imageAssets = useSelector(
    (state: RootState) => state.imageAsset.imageAssets
  );
  const showHidden = useSelector(
    (state: RootState) => state.imageAsset.showHidden
  );
  const searchQuery = useSelector(
    (state: RootState) => state.imageAsset.searchQuery
  );
  const pool = useSelector((state: RootState) => state.imageAsset.pool);
  const dispatch = useDispatch();
  const editEnabled = canUseEditSuite();

  // DV-2856: every "all"-scoped number below describes what the grid is
  // showing, so a search narrows them the same way the hidden filter already
  // does. Without this, Select All under a query checks 3 of 20 photos and the
  // header checkbox reads indeterminate forever.
  const filters = { showHidden, searchQuery, pool };
  const displayedAssets = displayedPhotos(imageAssets, filters);

  // Hidden photos never count as "selected" for bulk actions (download/hide
  // sync scope excludes hidden — AC: hidden is excluded from sync + bulk download).
  const selectedAssets = bulkSelection(imageAssets, filters);
  // Batch staging operates on the selected photos that can actually be staged
  // (not already a staged result, and not a floor plan — see canStagePhoto).
  // Same list the page hands the staging modal (handleOpenBatchStaging), so the
  // button's enablement and what the customer is billed for are one pool.
  const stageableSelectedCount = stageableSelection(
    imageAssets,
    filters
  ).length;
  // Hidden photos are parked out of the working set, so the live count only
  // reflects what's actually being managed (design: ma-gallery.jsx#useGallery).
  const imageCount = displayedAssets.filter((asset) => !asset.hidden).length;
  // NOT narrowed by the search: this labels "Show Hidden Photos (N)", which is
  // a fact about the photoset, not the current view — filtering it would make
  // the count jump around as the user types. Narrowed by the tab's pool, which
  // is what the toggle would reveal there.
  const hiddenCount = imageAssets.filter(
    (asset) => asset.hidden && isInPool(asset, pool)
  ).length;
  const selectedCount = selectedAssets.length;
  // DV-2524: delete is the ONE bulk action whose pool includes hidden photos.
  // selectedAssets above filters them out (download/sync/hide semantics), but a
  // hidden photo is a prime delete candidate and is only checkable with Show
  // Hidden on — so an explicit selection must be honored. Same list the delete
  // dialog receives (handleOpenDeleteSelected): the bar cannot say 2 and delete 5.
  const deletableSelectedCount = deletableSelection(
    imageAssets,
    filters
  ).length;
  const hasSelection = selectedCount > 0;
  // Decided 2026-09-18: a selection freezes the view. While anything is
  // checked the view filters are locked, which is what keeps the selection a
  // subset of what is displayed — see assetViewScope#hasCheckedAssets. Note
  // this is EVERY checkmark, hidden ones included: a hidden-only selection
  // (DV-2524's Trash case) holds the filters too, or releasing the lock would
  // be the very edit that strands it.
  const hasCheckedPhotos = hasCheckedAssets(imageAssets);
  const hasPhotos = imageCount > 0;
  const isGlobalChecked = selectedCount === imageCount && imageCount > 0;
  const isIndeterminate = selectedCount > 0 && selectedCount < imageCount;

  const handleHideSelected = () => {
    dispatch(hideMany(selectedAssets.map((a) => a.photoUUID)));
    // One PATCH per asset — the contract is per-asset partial update.
    selectedAssets.forEach((a) =>
      persistAssetPatch({ assetUUID: a.assetUUID, hidden: true }).catch(() => {
        dispatch(toggleHide(a.photoUUID));
        dispatch(
          showAlert({
            type: Variants.ERROR,
            message:
              'Some photos could not be hidden and have been restored — please try again.'
          })
        );
      })
    );
  };

  // Download semantics: nothing checked → "Download All" (the displayed,
  // non-hidden pool); anything checked → "Download Selected" (exactly what is
  // checked). Driven by "is anything checked", full stop (decided 2026-09-18,
  // Q2b) — that is the question selectionFromGrid itself asks, so the label
  // states the action rather than re-deriving it from two counts.
  const downloadLabel = hasCheckedPhotos ? 'Download Selected' : 'Download All';

  const toggleSelectAll = () => dispatch(toggleAllImageAssets());
  const setShowHiddenPhotos = (checked: boolean) =>
    dispatch(setShowHiddenAction(checked));

  return {
    editEnabled,
    showHidden,
    imageCount,
    hiddenCount,
    selectedCount,
    hasSelection,
    hasPhotos,
    filtersLocked: hasCheckedPhotos,
    isGlobalChecked,
    isIndeterminate,
    stageableSelectedCount,
    deletableSelectedCount,
    downloadLabel,
    handleHideSelected,
    toggleSelectAll,
    setShowHiddenPhotos
  };
}
